import React, { useState } from 'react';
import {
 Megaphone,
 CheckCircle,
 XCircle,
 Clock,
 Calendar,
 Plus,
 Edit,
 Trash2,
 Loader2,
 ListChecks,
} from 'lucide-react';
import { Button, SearchSelect } from '../../components/shared';
import DataTable from '../../components/shared/DataTable';
import DatePicker from '../../components/client/DatePicker';
import AdminAnnouncementModal from '../../components/admin/AdminAnnouncementModal';
import PillToggle from '../../components/shared/PillToggle';
import { useAdminAnnouncements } from '../../hooks/useAdminAnnouncements';
import { formatDate } from '../../utils/dateUtils';
import { AdminPageHeader, AdminGuideCallout } from '../../components/admin/guidance';
import { ADMIN_PAGE_GUIDES } from '../../utils/adminPageGuides';

const AdminAnnouncements = () => {
 const [showCreateModal, setShowCreateModal] = useState(false);
 const [editingAnnouncement, setEditingAnnouncement] = useState(null);
 const {
 announcements,
 isLoading,
 error,
 filters,
 activeStatusOptions,
 announcementStatusOptions,
 targetAudienceOptions,
 handleFilterChange,
 handleSearch,
 statistics,
 getAnnouncementStatus,
 toggleStatus,
 isToggling,
 deleteAnnouncement
 } = useAdminAnnouncements();

 const columns = [
 {
 key: 'title',
 label: 'Title',
 sortable: true,
 width: 'w-64',
 render: (announcement) => (
 <p className="text-sm font-medium text-ink">
 {announcement.title || 'N/A'}
 </p>
 )
 },
 {
 key: 'announcement_type',
 label: 'Type',
 sortable: true,
 width: 'w-32',
 render: (announcement) => (
 <span className="text-sm font-medium text-ink capitalize">
 {announcement.announcement_type || 'General'}
 </span>
 )
 },
 {
 key: 'target_audience',
 label: 'Audience',
 sortable: true,
 width: 'w-32',
 render: (announcement) => (
 <span className="text-sm font-medium text-ink capitalize">
 {announcement.target_audience || 'All'}
 </span>
 )
 },
 {
 key: 'dates',
 label: 'Schedule',
 sortable: false,
 width: 'w-48',
 render: (announcement) => (
 <div className="text-xs text-ink-secondary">
 {announcement.start_date && (
 <div>
 <span className="font-medium">Start:</span> {formatDate(announcement.start_date, 'short')}
 </div>
 )}
 {announcement.end_date && (
 <div>
 <span className="font-medium">End:</span> {formatDate(announcement.end_date, 'short')}
 </div>
 )}
 {!announcement.start_date && !announcement.end_date && (
 <span className="text-gray-400">No schedule</span>
 )}
 </div>
 )
 },
 {
 key: 'created_at',
 label: 'Created',
 sortable: true,
 width: 'w-32',
 render: (announcement) => (
 <div className="flex items-center space-x-2">
 <Calendar className="w-4 h-4 text-gray-400 flex-shrink-0" />
 <div className="min-w-0">
 <div className="text-sm font-medium text-ink">
 {formatDate(announcement.created_at, 'medium')}
 </div>
 </div>
 </div>
 )
 },
 {
 key: 'status',
 label: 'Status',
 sortable: true,
 width: 'w-32',
 render: (announcement) => {
 const status = getAnnouncementStatus(announcement);
 const statusConfig = {
 active: { color: 'bg-emerald-100 text-emerald-800', label: 'Active' },
 upcoming: { color: 'bg-surface-muted text-ink', label: 'Upcoming' },
 expired: { color: 'bg-gray-100 text-gray-800', label: 'Expired' },
 inactive: { color: 'bg-red-100 text-red-800', label: 'Inactive' }
 };
 const config = statusConfig[status] || statusConfig.inactive;

 return (
 <div className="flex items-center gap-2">
 <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${config.color}`}>
 {config.label}
 </span>
 <PillToggle
 checked={Boolean(announcement.is_active)}
 onChange={() => toggleStatus(announcement.id)}
 loading={isToggling}
 variant={announcement.is_active ? 'success' : 'amber'}
 onLabel="On"
 offLabel="Off"
 ariaLabel={`Toggle announcement ${announcement.title || announcement.id}`}
 />
 </div>
 );
 }
 }
 ];

 const actions = [
 {
 label: 'Edit',
 icon: Edit,
 variant: 'default',
 onClick: (announcement) => {
 setEditingAnnouncement(announcement);
 setShowCreateModal(true);
 }
 },
 {
 label: 'Delete',
 icon: Trash2,
 variant: 'danger',
 onClick: (announcement) => {
 if (window.confirm('Are you sure you want to delete this announcement?')) {
 deleteAnnouncement(announcement.id);
 }
 }
 }
 ];

 const { totalAnnouncements, activeAnnouncements, upcomingAnnouncements } = statistics;

 if (error) {
 return (
 <div className="space-y-5">
 <AdminPageHeader
 title="Announcements"
 subtitle="Manage platform-wide announcements and notifications"
 />
 <div className="rounded-lg border border-red-200 bg-surface p-6 text-center shadow-sm">
 <XCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
 <h3 className="text-xl font-bold text-ink mb-2">Error Loading Announcements</h3>
 <p className="text-ink-secondary">
 {error?.response?.data?.message || 'Failed to load announcements. Please try again later.'}
 </p>
 </div>
 </div>
 );
 }

 return (
 <div className="space-y-5">
 <AdminPageHeader
 title="Announcements"
 subtitle="Manage platform-wide announcements and notifications"
 actions={
 <Button
 className="rounded-md flex items-center gap-2"
 onClick={() => {
 setEditingAnnouncement(null);
 setShowCreateModal(true);
 }}
 >
 <Plus className="w-4 h-4" />
 Create Announcement
 </Button>
 }
 />

 <AdminGuideCallout
 title={ADMIN_PAGE_GUIDES.announcements.title}
 steps={ADMIN_PAGE_GUIDES.announcements.steps}
 icon={ListChecks}
 />

 <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
 <div className="product-panel p-4 sm:p-5">
 <div className="flex items-center justify-between">
 <div>
 <p className="text-sm text-ink-secondary">Total Announcements</p>
 <p className="text-2xl font-bold text-ink mt-1 tabular-nums">{totalAnnouncements}</p>
 </div>
 <Megaphone className="w-8 h-8 text-gray-400" />
 </div>
 </div>
 <div className="product-panel p-4 sm:p-5">
 <div className="flex items-center justify-between">
 <div>
 <p className="text-sm text-ink-secondary">Active</p>
 <p className="text-2xl font-bold text-emerald-600 mt-1 tabular-nums">{activeAnnouncements}</p>
 </div>
 <CheckCircle className="w-8 h-8 text-emerald-500" />
 </div>
 </div>
 <div className="product-panel p-4 sm:p-5">
 <div className="flex items-center justify-between">
 <div>
 <p className="text-sm text-ink-secondary">Upcoming</p>
 <p className="text-2xl font-bold text-ink mt-1 tabular-nums">{upcomingAnnouncements}</p>
 </div>
 <Clock className="w-8 h-8 text-ink-secondary" />
 </div>
 </div>
 </div>

 <div className="product-panel overflow-hidden">
 <div className="p-4 sm:p-5 space-y-5">
 <div className="rounded-xl border border-border bg-surface-muted p-4">
 <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
 <SearchSelect
 label="Active Status"
 value={filters.is_active}
 onChange={(value) => handleFilterChange('is_active', value)}
 options={activeStatusOptions}
 />
 <SearchSelect
 label="Announcement Status"
 value={filters.status}
 onChange={(value) => handleFilterChange('status', value)}
 options={announcementStatusOptions}
 />
 <SearchSelect
 label="Target Audience"
 value={filters.target_audience}
 onChange={(value) => handleFilterChange('target_audience', value)}
 options={targetAudienceOptions}
 />
 <DatePicker
 label="From Date"
 value={filters.date_from}
 onChange={(value) => handleFilterChange('date_from', value)}
 />
 <DatePicker
 label="To Date"
 value={filters.date_to}
 onChange={(value) => handleFilterChange('date_to', value)}
 />
 </div>
 </div>

 {isLoading ? (
 <div className="py-20 text-center">
 <Loader2 className="w-8 h-8 animate-spin text-ink mx-auto mb-3" />
 <p className="text-sm text-ink-tertiary">Loading announcements…</p>
 </div>
 ) : (
 <DataTable
 data={announcements}
 columns={columns}
 actions={actions}
 isLoading={false}
 searchPlaceholder="Search announcements by title, content, or type..."
 onSearch={handleSearch}
 emptyMessage="No announcements found"
 className="!border-0 !shadow-none"
 />
 )}
 </div>
 </div>

 <AdminAnnouncementModal
 isOpen={showCreateModal}
 onClose={() => {
 setShowCreateModal(false);
 setEditingAnnouncement(null);
 }}
 announcement={editingAnnouncement}
 />
 </div>
 );
};

export default AdminAnnouncements;
